import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");

const AeroTable8 = ({ data = [] }) => {
  const pdfRef = useRef(null);

  const cleaned = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Remove an "Average" row if it exists (some APIs include it as a label)
    return data.filter(
      (r) => String(r["Age Group Label"] || "").toLowerCase() !== "average",
    );
  }, [data]);

  const ageGroups = useMemo(
    () => cleaned.map((item) => item["Age Group Label"]),
    [cleaned],
  );

  const averageFemale = useMemo(
    () => cleaned.map((item) => num(item["Average of Female"])),
    [cleaned],
  );

  const averageMale = useMemo(
    () => cleaned.map((item) => num(item["Average of Male"])),
    [cleaned],
  );

  const computed = useMemo(() => {
    if (cleaned.length === 0) return null;

    const totalFemale = averageFemale.reduce((a, b) => a + b, 0);
    const totalMale = averageMale.reduce((a, b) => a + b, 0);

    const overallFemaleAvg = cleaned.length ? totalFemale / cleaned.length : 0;
    const overallMaleAvg = cleaned.length ? totalMale / cleaned.length : 0;

    // Find top age group for female and male
    let topFemaleIdx = 0;
    let topMaleIdx = 0;

    for (let i = 1; i < cleaned.length; i++) {
      if (averageFemale[i] > averageFemale[topFemaleIdx]) topFemaleIdx = i;
      if (averageMale[i] > averageMale[topMaleIdx]) topMaleIdx = i;
    }

    const combined = cleaned.map((_, i) => averageFemale[i] + averageMale[i]);
    let topCombinedIdx = 0;
    for (let i = 1; i < combined.length; i++) {
      if (combined[i] > combined[topCombinedIdx]) topCombinedIdx = i;
    }

    return {
      overallFemaleAvg,
      overallMaleAvg,
      topFemale: {
        label: ageGroups[topFemaleIdx],
        value: averageFemale[topFemaleIdx],
      },
      topMale: {
        label: ageGroups[topMaleIdx],
        value: averageMale[topMaleIdx],
      },
      topCombined: {
        label: ageGroups[topCombinedIdx],
        value: combined[topCombinedIdx],
      },
    };
  }, [cleaned, averageFemale, averageMale, ageGroups]);

  const chartData = useMemo(() => {
    return {
      labels: ageGroups,
      datasets: [
        {
          label: "Average Female Workers",
          data: averageFemale,
          backgroundColor: "#EC489980",
          borderColor: "#EC4899",
          borderWidth: 1,
          borderRadius: 6,
        },
        {
          label: "Average Male Workers",
          data: averageMale,
          backgroundColor: "#0ea5e980",
          borderColor: "#0ea5e9",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [ageGroups, averageFemale, averageMale]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "Average Employment by Age Group (2018-2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      legend: {
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Age Group" },
        grid: { display: false },
        ticks: {
          font: { size: window.innerWidth < 768 ? 10 : 12 },
        },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Average Workers" },
        ticks: {
          callback: (v) => fmt(v),
        },
        grid: { display: true },
      },
    },
  };

    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text("20250916.v3.198", 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );
        pdf.save("avg-employment-by-age-and-gender.pdf");
      });
    };

  if (!data || data.length === 0)
    return <p>No data available for Employment Trends.</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Age and Gender (Averages, 2018–2024)
        </h2>

        {/* Summary Cards */}
        {computed && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
            <div className="p-4 bg-pink-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Overall Avg Female
              </p>
              <p className="text-xl font-bold text-pink-800">
                {fmt(computed.overallFemaleAvg)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Top: {computed.topFemale.label} ({fmt(computed.topFemale.value)}
                )
              </p>
            </div>

            <div className="p-4 bg-blue-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Overall Avg Male
              </p>
              <p className="text-xl font-bold text-blue-800">
                {fmt(computed.overallMaleAvg)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Top: {computed.topMale.label} ({fmt(computed.topMale.value)})
              </p>
            </div>

            <div className="p-4 bg-green-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Top Combined Age Group
              </p>
              <p className="text-xl font-bold text-green-800">
                {computed.topCombined.label}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Total Avg: {fmt(computed.topCombined.value)}
              </p>
            </div>
          </div>
        )}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left text-sm">Age Group</th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Avg Female Workers
                    </th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Avg Male Workers
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {cleaned.map((item, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${
                        index % 2 ? "bg-gray-50/60" : "bg-white"
                      }`}
                    >
                      <td className="p-2 text-left text-sm">
                        {item["Age Group Label"]}
                      </td>
                      <td
                        className="p-2 text-sm"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {num(item["Average of Female"]).toFixed(2)}
                      </td>
                      <td
                        className="p-2 text-sm"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {num(item["Average of Male"]).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally on mobile if needed.
            </p> */}
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable8;
